import { useContext } from "react";
import { Remove2faContext } from "./context";
import { Remove2faContextType } from "./types";
import React from "react";
import {
  MfaCodeStrategy,
  Remove2faStrategyResult,
  Setup2faStrategyResult,
  Success,
  Verify2faStrategyResult,
} from "@towersystems/roam-common/lib/generated-types";
import { userIsAdmin } from "../user-is-admin";
import {
  RequestMfaCodeMutation,
  useAuthenticationProvider,
} from "@towersystems/roam-common-native/lib/utilities/authentication";
import { useFormContext } from "react-hook-form";
import { SetupNative2faFormValues } from "../../modules/settings/components/SetupNative2faForm/types";
import { useCurrentVendorProvider } from "../vendor";
import { useMutation } from "@apollo/client";
import {
  Remove2faStrategyMutation,
  Setup2faStrategyMutation,
  Verify2faStrategyMutation,
} from "./constants";
import { Remove2faFormValues } from "../../modules/settings/components/Remove2faForm/types";

export const useRemove2fa = (): Remove2faContextType => {
  const [strategyCode, setStrategyCode] = React.useState<
    MfaCodeStrategy | undefined
  >(undefined);
  const [open, setOpen] = React.useState(false);

  const handleOnOpen = (strategyCode: MfaCodeStrategy) => {
    setStrategyCode(strategyCode);
    setOpen(true);
  };

  const handleOnClose = () => {
    setOpen(false);
    setStrategyCode(undefined);
  };

  return {
    strategyCode,
  };
};

export const useRemove2faProvider = (): Remove2faContextType => {
  const context = useContext(Remove2faContext);
  if (!context) {
    throw new Error("No Remove2faContext context was provided.");
  }
  return context;
};

export const useSetup2fa = () => {
  const { user } = useAuthenticationProvider();

  const formContext = useFormContext<SetupNative2faFormValues>();
  const { setError, setValue } = formContext;
  const { currentVendor } = useCurrentVendorProvider();

  const [setup2faStrategyFn] = useMutation<{
    setup2faStrategy: Setup2faStrategyResult;
  }>(Setup2faStrategyMutation);

  const [verify2faStrategyFn] = useMutation<{
    verify2faStrategy: Verify2faStrategyResult;
  }>(Verify2faStrategyMutation);

  function handleOnSubmit(values: SetupNative2faFormValues) {
    if (!user) {
      setError("root.serverError", {
        type: "400",
        message: "error.user-not-authenticated",
      });
      return;
    }

    const { data, ui } = values;

    if (false === ui.codeSent) {
      return setup2faStrategyFn({
        variables: {
          input: {
            strategy: data.strategy,
            identifier: data.identifier,
          },
        },
      })
        .then((result) => {
          if (result.data) {
            if ("errorCode" in result.data.setup2faStrategy) {
              setError("root.serverError", {
                type: "400",
                message: result.data.setup2faStrategy.errorCode,
              });
            } else {
              setValue("ui.codeSent", true);
            }
          }
        })
        .catch((err) => {
          setError("root.serverError", {
            type: "400",
            message: err.message,
          });
        });
    }

    if (true === ui.codeSent) {
      const currentUserIsOwner = userIsAdmin(user);

      let credentialsInput;

      if (currentUserIsOwner) {
        credentialsInput = {
          ["talink"]: {
            password: data.password,
            talinkUsername: user.identifier,
          },
        };
      } else {
        credentialsInput = {
          ["native"]: {
            password: data.password,
            username: user.identifier,
            talinkUsername: currentVendor?.talinkUsername,
          },
        };
      }

      return verify2faStrategyFn({
        variables: {
          credentials: credentialsInput,
          code: data.code,
          strategy: data.strategy,
        },
      })
        .then((result) => {
          if (result.data) {
            if ("errorCode" in result.data.verify2faStrategy) {
              setError("root.serverError", {
                type: "400",
                message: result.data.verify2faStrategy.errorCode,
              });
              return;
            } else {
              return true;
            }
          }
        })
        .catch((err) => {
          setError("root.serverError", {
            type: "400",
            message: err.message,
          });
        });
    }

    setError("root.serverError", {
      type: "400",
      message: "error.something-went-wrong",
    });
  }

  return { handleOnSubmit };
};

export function useRemove2faStrategy() {
  const formContext = useFormContext<Remove2faFormValues>();
  const { setError, setValue } = formContext;
  const { currentVendor } = useCurrentVendorProvider();
  const { user } = useAuthenticationProvider();

  const [requestMfaCodeFn] = useMutation<{
    requestMfaCode: Success;
  }>(RequestMfaCodeMutation);

  const [removeMfaStrategyFn] = useMutation<{
    remove2faStrategy: Remove2faStrategyResult;
  }>(Remove2faStrategyMutation);

  const handleOnSubmit = React.useCallback(
    (values: Remove2faFormValues) => {
      if (!user) {
        setError("root.serverError", {
          type: "400",
          message: "error.user-not-authenticated",
        });
        return;
      }

      const { data, ui } = values;

      if (false === ui.codeSent) {
        const currentUserIsOwner = userIsAdmin(user);

        let credentialsInput;

        if (currentUserIsOwner) {
          credentialsInput = {
            ["talink"]: {
              password: data.password,
              talinkUsername: user.identifier,
            },
          };
        } else {
          credentialsInput = {
            ["native"]: {
              password: data.password,
              username: user.identifier,
              talinkUsername: currentVendor?.talinkUsername,
            },
          };
        }

        return requestMfaCodeFn({
          variables: {
            input: credentialsInput,
            mfaStrategy: data.strategy,
          },
        })
          .then((result) => {
            if (result.data) {
              setValue("ui.codeSent", true);
            }
          })
          .catch((err) => {
            setError("root.serverError", {
              type: "400",
              message: err.message,
            });
          });
      } else {
        return removeMfaStrategyFn({
          variables: {
            strategy: data.strategy,
            code: data.code,
          },
        })
          .then((result) => {
            if (result.data) {
              if ("errorCode" in result.data?.remove2faStrategy) {
                setError("root.serverError", {
                  type: "400",
                  message: result.data.remove2faStrategy.errorCode,
                });
                return;
              } else {
                return true;
              }
            }
          })
          .catch((err) => {
            setError("root.serverError", {
              type: "400",
              message: err.message,
            });
          });
      }
    },
    [user, currentVendor]
  );

  return { handleOnSubmit };
}
